import React, { useEffect, useState } from 'react'

import { formatMoneyFull } from '../../../../utils/formatters'
import { fetchProgramCapitalReleaseHistory } from '../services/investmentProgramApi'

const requestMessage = (error) => error?.response?.status === 403
  ? 'You do not have permission to view capital release history.'
  : 'Capital release history could not be loaded. Try again.'

export default function CapitalReleaseHistory({ programReference, projectReference, refreshKey = 0 }) {
  const [open, setOpen] = useState(false)
  const [history, setHistory] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true); setError('')
    try {
      setHistory(await fetchProgramCapitalReleaseHistory({ programReference, projectReference }))
    } catch (requestError) {
      setError(requestMessage(requestError))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && history !== null) load()
    // A successful release invalidates already-disclosed evidence; unopened history stays lazy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next && history === null && !loading) load()
  }

  return <div className="w-full min-w-[15rem]">
    <button type="button" className="font-semibold text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-300" aria-expanded={open} aria-controls={`capital-release-history-${projectReference}`} onClick={toggle}>
      {open ? 'Hide release history' : 'View release history'}
    </button>
    {open && <section id={`capital-release-history-${projectReference}`} aria-label={`Capital release history for ${projectReference}`} className="mt-3 rounded-lg border bg-slate-50 p-3">
      {loading && <p role="status" className="text-xs text-slate-600">Loading release history…</p>}
      {!loading && error && <div role="alert" className="text-xs text-red-700"><p>{error}</p><button type="button" className="mt-2 font-semibold underline" onClick={load}>Try again</button></div>}
      {!loading && !error && history?.length === 0 && <p className="text-xs text-slate-600">No capital releases have been recorded.</p>}
      {!loading && !error && history?.length > 0 && <ol className="space-y-3">
        {history.map((release) => <li key={release.release_reference} className="border-l-2 border-blue-300 pl-3 text-xs text-slate-700">
          <p className="font-semibold text-slate-900">{formatMoneyFull(Number(release.amount), release.currency)} ({release.currency}) released</p>
          <p><time dateTime={release.effective_at}>{new Date(release.effective_at).toLocaleString()}</time> · Authority V{release.authorization_version_number}</p>
          <p className="break-all font-mono text-[0.7rem]">{release.authorization_reference}</p>
          <p className="mt-1">Outstanding: {formatMoneyFull(Number(release.previous_outstanding_commitment), release.currency)} → {formatMoneyFull(Number(release.resulting_outstanding_commitment), release.currency)}</p>
          <p className="mt-1 break-words"><span className="font-semibold">Reason:</span> {release.reason}</p>
          {release.released_by && <p className="mt-1">Recorded by {release.released_by}</p>}
        </li>)}
      </ol>}
    </section>}
  </div>
}
